import { Collapse, ConfigProvider } from 'antd';
import React, { useState, useEffect, useRef } from 'react';
import styles from './section.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretRight } from '@fortawesome/pro-solid-svg-icons';
import { ActionsDropdown } from '../actions-dropdown/actions-dropdown.component';
import { Tooltip } from 'src/components/core';
import { colorPalette } from 'shared/constants';

export function SectionComponent({
  title,
  adminActions,
  customActions = null,
  badge = null,
  children,
  showActions = false,
  defaultIsOpen = true,
  isOpen: controlledIsOpen,
  onToggle = () => {},
  backgroundColor = colorPalette.primaryMain1,
  disabled = false,
  tooltip,
}) {
  const isControlled = controlledIsOpen !== undefined;
  const [userToggled, setUserToggled] = useState(false);
  const [internalIsOpen, setInternalIsOpen] = useState(
    isControlled ? controlledIsOpen : defaultIsOpen,
  );
  const collapseRef = useRef(null);

  // In uncontrolled mode, follow defaultIsOpen prop unless the user has manually toggled
  const isSectionOpen = isControlled
    ? controlledIsOpen
    : userToggled
      ? internalIsOpen
      : defaultIsOpen;

  const handleChange = keys => {
    const open = keys.length > 0;
    if (!isControlled) {
      setUserToggled(true);
      setInternalIsOpen(open);
    }
    onToggle(open);
  };

  // Handle Escape key to close the section
  useEffect(() => {
    const element = collapseRef.current;
    if (!element) return;

    const handleKeyDown = event => {
      // Don't close section if a dropdown is open (e.g., Select component)
      const hasOpenDropdown = document.querySelector(
        '.ant-select-dropdown:not(.ant-select-dropdown-hidden)',
      );
      if (hasOpenDropdown) return;

      if (event.key === 'Escape' && isSectionOpen) {
        if (!isControlled) {
          setUserToggled(true);
          setInternalIsOpen(false);
        }
        onToggle(false);
      }
    };

    element.addEventListener('keydown', handleKeyDown);
    return () => element.removeEventListener('keydown', handleKeyDown);
  }, [isSectionOpen, isControlled, onToggle]);

  function renderHeader() {
    return (
      <div className={styles.header}>
        <div className={styles.titleWrapper}>
          <h5 className={styles.title}>{title}</h5>
          {badge}
        </div>
        {showActions && <ActionsDropdown actions={adminActions} />}
        {customActions}
      </div>
    );
  }

  const collapseItems = [
    {
      key: '1',
      label: renderHeader(),
      collapsible: disabled ? 'disabled' : undefined,
      children: (
        <div className={styles.items} style={{ backgroundColor }}>
          {children}
        </div>
      ),
    },
  ];

  const sectionElement = (
    <div ref={collapseRef} className={disabled ? styles.disabled : undefined}>
      <ConfigProvider
        theme={{
          components: {
            Collapse: {
              headerPadding: '8px 16px',
              headerMargin: 0,
              contentPadding: 0,
              headerBg: 'transparent',
              colorBorder: '#bac8f0',
              borderRadius: 4,
              headerBorderRadius: 4,
            },
          },
        }}>
        <Collapse
          rootClassName={styles.root}
          className={styles.collapse}
          activeKey={isSectionOpen ? ['1'] : []} // Dynamically control open state
          onChange={handleChange}
          variant="borderless"
          style={{ border: 'none' }}
          expandIcon={({ isActive }) => (
            <FontAwesomeIcon
              className={styles.icon}
              icon={faCaretRight}
              rotation={isActive ? 90 : 0}
            />
          )}
          items={collapseItems}
        />
      </ConfigProvider>
    </div>
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{sectionElement}</Tooltip>;
  }
  return sectionElement;
}
